export interface BackendError {
  code?: string;
  message?: string;
  errors?: { [key: string]: string[] };
}

import { I18nService } from '../services/i18n.service';

export interface ErrorMapping {
  title: string;
  message: string;
}

let i18nServiceInstance: I18nService | null = null;

export function setI18nService(instance: I18nService): void {
  i18nServiceInstance = instance;
}

function translate(key: string, params?: Record<string, string>): string {
  return i18nServiceInstance ? i18nServiceInstance.translate(key, params) : key;
}

export const ERROR_MAPPINGS: { [key: string]: ErrorMapping } = {
  'Authentication.MissingToken': {
    title: translate('errors.defaultTitle'),
    message: translate('notifications.sessionExpired')
  },
  'Authentication.Token.InvalidOrExpired': {
    title: translate('errors.auth.tokenInvalidOrExpired'),
    message: translate('errors.auth.tokenInvalidOrExpiredMessage')
  },
  'Authentication.InvalidToken': {
    title: translate('errors.auth.tokenInvalid'),
    message: translate('errors.auth.tokenInvalidMessage')
  },
  'Authentication.TenantNotAllowed': {
    title: translate('errors.auth.tenantNotAllowed'),
    message: translate('errors.auth.tenantNotAllowedMessage')
  },
  'Authentication.UserNotFound': {
    title: translate('errors.auth.userNotFound'),
    message: translate('errors.auth.userNotFoundMessage')
  },
  'Authentication.InvalidState': {
    title: translate('errors.auth.invalidState'),
    message: translate('errors.auth.invalidStateMessage')
  },
  'Authentication.CodeAlreadyUsed': {
    title: translate('errors.auth.codeAlreadyUsed'),
    message: translate('errors.auth.codeAlreadyUsedMessage')
  },
  'Authentication.Callback.MissingCode': {
    title: translate('errors.auth.missingCode'),
    message: translate('errors.auth.missingCodeMessage')
  },
  'Authentication.Callback.MissingState': {
    title: translate('errors.auth.missingState'),
    message: translate('errors.auth.missingStateMessage')
  },
  'Authentication.ValidationError': {
    title: translate('errors.auth.validationError'),
    message: translate('errors.auth.validationErrorMessage')
  },
  'Authentication.InitiateLogin.Failed': {
    title: translate('errors.auth.initiateLoginFailed'),
    message: translate('errors.auth.initiateLoginFailedMessage')
  },
  'Authentication.ExchangeFailed': {
    title: translate('errors.auth.exchangeFailed'),
    message: translate('errors.auth.exchangeFailedMessage')
  },
  'Authentication.UnexpectedError': {
    title: translate('errors.auth.unexpectedError'),
    message: translate('errors.auth.unexpectedErrorMessage')
  },
  'Paciente.AcessoNegado': {
    title: translate('notifications.accessDenied'),
    message: translate('patient.errors.accessDenied')
  },
  'Paciente.AlteracaoNegada': {
    title: translate('notifications.accessDenied'),
    message: translate('patient.errors.editAccessDenied')
  },
  'Paciente.AlteracaoIdentificacaoNegada': {
    title: translate('notifications.accessDenied'),
    message: translate('patient.errors.identificationEditAccessDenied')
  },
  'Solicitante.AcessoNegado': {
    title: translate('notifications.accessDenied'),
    message: translate('requester.errors.accessDenied')
  },
  'Solicitante.AlteracaoNegada': {
    title: translate('notifications.accessDenied'),
    message: translate('requester.errors.editAccessDenied')
  }
};

const LAZY_ERROR_MSG_KEYS: Record<string, string> = {
  'CollectionQueueGrid.PeriodoInvalido': 'attendance.collectionQueue.errors.invalidPeriod',
  'CollectionQueueGrid.PeriodoMaiorQue30Dias': 'attendance.collectionQueue.errors.invalidInterval',
  'Message.RecipientNotFound': 'messages.notifications.recipientNotFound',
};

export function getErrorMessage(error: BackendError | any, defaultMessage?: string, i18n?: I18nService): { title: string; message: string } {
  const errorCode =
    error?.code ||
    (error as any)?.Code ||
    error?.error?.code ||
    (error as any)?.error?.Code;
  const errorMessage =
    error?.message ||
    (error as any)?.Message ||
    error?.error?.message ||
    (error as any)?.error?.Message;
  const translateFn = i18n
    ? (key: string, params?: Record<string, string>) => i18n.translate(key, params)
    : (key: string, _params?: Record<string, string>) => key;

  if (errorCode && ERROR_MAPPINGS[errorCode]) {
    return ERROR_MAPPINGS[errorCode];
  }

  const lazyMsgKey = errorCode && LAZY_ERROR_MSG_KEYS[errorCode];
  if (lazyMsgKey) {
    const params = errorMessage ? { users: errorMessage } : undefined;
    return {
      title: translateFn('errors.validationTitle'),
      message: translateFn(lazyMsgKey, params)
    };
  }

  if (typeof errorCode === 'string' && errorCode.startsWith('Authentication.')) {
    return {
      title: translateFn('errors.defaultTitle'),
      message: translateFn('errors.auth.tokenInvalidMessage')
    };
  }

  if (errorMessage) {
    return {
      title: translateFn('errors.defaultTitle'),
      message: errorMessage
    };
  }

  if (error?.errors && typeof error.errors === 'object') {
    const firstError = Object.values(error.errors)[0];
    const message = Array.isArray(firstError) ? firstError[0] : String(firstError);
    return {
      title: translateFn('errors.validationTitle'),
      message: message
    };
  }

  return {
    title: translateFn('errors.defaultTitle'),
    message: defaultMessage || translateFn('errors.defaultMessage')
  };
}
